import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    User,
    CalendarDays,
    Utensils,
    PhoneCall,
    CreditCard,
    Receipt,
    CircleHelp,
    Bell,
    Search,
    ChevronDown,
    ChevronRight,
    Info,
    Activity,
    Heart,
    Apple,
    Target,
    BookOpen,
    Menu,
    X,
    Library,
    Crown,
    Award
} from "lucide-react";
import { Pencil } from 'lucide-react';
import { Clock } from 'lucide-react';
import { Video } from 'lucide-react';
import { Coffee, Salad, UtensilsCrossed } from 'lucide-react';
import { Phone } from 'lucide-react';
import { Paperclip } from 'lucide-react';
import { getUserData, getAuthData, isLoggedIn, clearAuthData } from "../../api/storageService";
import { 
    getWebDashboard, 
    appointmentInquiry, 
    createFollowUp, 
    getNotifications, 
    getUnreadCount, 
    markNotificationAsRead, 
    markAllNotificationsAsRead 
} from "../../api/api";

import PersonalInfo from "./PersonalInfo";
import MedicalHistory from "./MedicalHistory";
import Lifestyle from "./Lifestyle";
import DietaryPreferences from "./DietaryPreferences";
import HealthGoals from "./HealthGoals";
import MyAppointments from "./MyAppointments";
import DietPlans from "./DietPlans";
import FollowupSessions from "./FollowupSessions";
import PurchasesPlans from "./PurchasesPlans";
import PaymentHistory from "./PaymentHistory";
import SupportEnquiry from "./SupportEnquiry";
import MyCourses from "./CoursesListingPage";
import CoursePlayer from "../CoursePlayer";
import Assessment from "../Assessments";
import LearningResources from "./LearningResources";
import DigitalLibrary from "./DigitalLibrary";
import AppointmentDetails from "./AppointmentDetails";

// Notification Dropdown Component
const NotificationDropdown = ({ onClose, onMarkAsRead, onUnreadCountUpdate }) => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchNotifications();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await getNotifications(0);

            if (response && response.success) {
                setNotifications(response.data?.notifications || []);
                const count = response.data?.unread_count || 0;
                setUnreadCount(count);
                if (onUnreadCountUpdate) onUnreadCountUpdate(count);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClick = async (item) => {
        if (!item.read_at) {
            try {
                await markNotificationAsRead(item.id);
                setNotifications(prev => prev.filter(n => n.id !== item.id));

                const newCount = Math.max(0, unreadCount - 1);
                setUnreadCount(newCount);
                if (onUnreadCountUpdate) onUnreadCountUpdate(newCount);
                if (onMarkAsRead) onMarkAsRead();
            } catch (error) {
                console.error("Error marking notification as read:", error);
            }
        }

        onClose();
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsAsRead();
            setNotifications([]);
            setUnreadCount(0);
            if (onUnreadCountUpdate) onUnreadCountUpdate(0);
            if (onMarkAsRead) onMarkAsRead();
        } catch (error) {
            console.error("Error marking all as read:", error);
        }
    };

    const getIconForType = (type) => {
        switch (type) {
            case 'course':
                return <BookOpen size={18} className="text-blue-500" />;
            case 'certificate':
                return <Award size={18} className="text-orange-500" />;
            case 'general':
            default:
                return <Bell size={18} className="text-[#15803d]" />;
        }
    };

    const getTimeAgo = (date) => {
        if (!date) return '';
        const now = new Date();
        const past = new Date(date);
        const diffMs = now - past;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        if (diffWeeks < 4) return `${diffWeeks}w ago`;
        if (diffMonths < 12) return `${diffMonths}mo ago`;
        return `${diffYears}y ago`;
    };

    return (
        <div ref={dropdownRef} className="absolute right-0 mt-2 w-[380px] max-h-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-100 z-50">
            <div className="flex items-center justify-between border-b px-5 py-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Bell size={20} className="text-[#15803d]" />
                    Notifications
                    {unreadCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-[#DCFCE7] text-[#15803d] text-xs font-medium rounded-full">
                            {unreadCount} new
                        </span>
                    )}
                </h2>
                {unreadCount > 0 && (
                    <button
                        onClick={handleMarkAllRead}
                        className="text-sm font-medium text-[#15803d] hover:text-green-700 transition-colors"
                    >
                        Mark all as read
                    </button>
                )}
            </div>

            <div className="max-h-[340px] overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#15803d]"></div>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="text-center py-8">
                        <Bell size={40} className="text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">No unread notifications</p>
                    </div>
                ) : (
                    notifications.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => handleNotificationClick(item)}
                            className="flex cursor-pointer items-start gap-4 border-b px-5 py-4 transition hover:bg-green-50 bg-green-50/50"
                        >
                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-pink-100 flex-shrink-0">
                                {getIconForType(item.type)}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">
                                        {item.title}
                                    </h4>
                                    <span className="w-2 h-2 bg-[#15803d] rounded-full flex-shrink-0"></span>
                                </div>
                                {item.description && (
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                        {item.description}
                                    </p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                    {getTimeAgo(item.createdAt)}
                                </p>
                                <span className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                    Unread
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {notifications.length > 0 && (
                <div className="border-t px-5 py-3 text-center">
                    <button
                        onClick={() => {
                            onClose();
                        }}
                        className="text-sm font-medium text-[#15803d] hover:text-green-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            )}
        </div>
    );
};

const DashboardOverview = () => {
    const navigate = useNavigate();
    const [isProfileOpen, setIsProfileOpen] = useState(true);
    const [activeComponent, setActiveComponent] = useState("dashboard");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [userData, setUserData] = useState(null);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [authChecking, setAuthChecking] = useState(true);
    const user = getUserData();
    const location = useLocation();
    const [courseSlug, setCourseSlug] = useState(null);
    const [assessmentData, setAssessmentData] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState(null);

    const [followUpSelectedDate, setFollowUpSelectedDate] = useState(null);
    const [followUpSelectedTime, setFollowUpSelectedTime] = useState(null);
    const [followUpMode, setFollowUpMode] = useState("Video Call");
    const [followUpLoading, setFollowUpLoading] = useState(false);
    const [isLearningResourcesOpen, setIsLearningResourcesOpen] = useState(false);
    const [selectedAppointmentForDetails, setSelectedAppointmentForDetails] = useState(null);

    // Notification states
    const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const [supportForm, setSupportForm] = useState({
        subject: "",
        message: "",
    });
    const [supportLoading, setSupportLoading] = useState(false);

    useEffect(() => {
        const checkAuth = () => {
            if (!isLoggedIn()) {
                clearAuthData();
                navigate("/login", { replace: true });
                return;
            }

            const user = getUserData();
            if (user) {
                setUserData(user);
            }

            setAuthChecking(false);
        };

        checkAuth();
    }, [navigate]);

    // Fetch unread count on mount
    useEffect(() => {
        fetchUnreadCount();
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const response = await getUnreadCount();
            if (response && response.success) {
                const count = response.data?.count || response.count || 0;
                setUnreadCount(count);
            }
        } catch (error) {
            console.error("Error fetching unread count:", error);
            setUnreadCount(0);
        }
    };

    const toggleNotificationDropdown = () => {
        setShowNotificationDropdown(!showNotificationDropdown);
    };

    const handleMarkAllAsRead = () => {
        setUnreadCount(0);
        fetchUnreadCount();
    };

    const handleUnreadCountUpdate = (count) => {
        setUnreadCount(count);
    };

    const learningResourcesSubItems = [
        { id: "digitallibrary", label: "Digital Library", icon: BookOpen },
    ];

    const toggleLearningResourcesDropdown = (e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        setIsLearningResourcesOpen(!isLearningResourcesOpen);
    };

    useEffect(() => {
        if (location.state?.activeComponent) {
            setActiveComponent(location.state.activeComponent);
            if (location.state?.courseSlug) {
                setCourseSlug(location.state.courseSlug);
            }
            if (location.state?.appointmentToken) {
                setSelectedAppointmentForDetails(location.state.appointmentToken);
            }
            if (location.state?.activeComponent === 'assessment') {
                setAssessmentData({
                    assessmentId: location.state.assessmentId,
                    assessmentTitle: location.state.assessmentTitle,
                    moduleId: location.state.moduleId,
                    moduleTitle: location.state.moduleTitle,
                    courseId: location.state.courseId,
                    courseTitle: location.state.courseTitle,
                    passingScore: location.state.passingScore,
                    totalQuestions: location.state.totalQuestions,
                    timeLimit: location.state.timeLimit,
                    assessmentUrl: location.state.assessmentUrl,
                    courseSlug: location.state.courseSlug
                });
            } else {
                setAssessmentData(null);
            }

            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const showToast = (message, type = 'success') => {
        const existingToast = document.querySelector('.custom-toast');
        if (existingToast) existingToast.remove();

        const toast = document.createElement('div');
        toast.className = `custom-toast toast-${type}`;

        const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
        const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';

        toast.innerHTML = `
            <div style="display: flex; align-items: center; gap: 12px;">
                <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
                    ${icon}
                </div>
                <div style="flex: 1;">${message}</div>
                <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
            </div>
        `;

        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: ${bgColor};
            color: white;
            padding: 14px 20px;
            border-radius: 12px;
            z-index: 9999;
            font-family: system-ui, -apple-system, sans-serif;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
            min-width: 300px;
            max-width: 400px;
            cursor: pointer;
            animation: slideInRight 0.3s ease;
            z-index: 10000;
        `;

        if (!document.querySelector('#toast-styles')) {
            const style = document.createElement('style');
            style.id = 'toast-styles';
            style.textContent = `
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes slideOutRight {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(100%); opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        }

        document.body.appendChild(toast);

        setTimeout(() => {
            if (toast && toast.parentElement) {
                toast.style.animation = 'slideOutRight 0.3s ease';
                setTimeout(() => {
                    if (toast && toast.parentElement) toast.remove();
                }, 300);
            }
        }, 3000);

        toast.onclick = () => {
            if (toast && toast.parentElement) {
                toast.style.animation = 'slideOutRight 0.3s ease';
                setTimeout(() => {
                    if (toast && toast.parentElement) toast.remove();
                }, 300);
            }
        };
    };

    const menuItems = [
        { id: "dashboard", icon: LayoutDashboard, label: "Dashboard Overview", active: true },
        { id: "profile", icon: User, label: "Manage Profile", hasDropdown: true },
        { id: "appointments", icon: CalendarDays, label: "My Appointments" },
        { id: "dietplans", icon: Utensils, label: "Diet Plans" },
        { id: "followup", icon: PhoneCall, label: "Follow-up Sessions" },
        { id: "mycourses", icon: BookOpen, label: "My Courses" },
        { id: "learningresources", icon: Library, label: "Learning Resources", hasDropdown: true },
    ];

    const profileMenuItems = [
        { id: "personalInfo", label: "Personal Info", icon: Info, component: <PersonalInfo setActiveComponent={setActiveComponent} /> },
        { id: "medicalHistory", label: "Medical History", icon: Activity, component: <MedicalHistory setActiveComponent={setActiveComponent} /> },
        { id: "lifestyle", label: "Lifestyle", icon: Heart, component: <Lifestyle setActiveComponent={setActiveComponent} /> },
        { id: "dietaryPreferences", label: "Dietary Preferences", icon: Apple, component: <DietaryPreferences setActiveComponent={setActiveComponent} /> },
        { id: "healthGoals", label: "Health Goals", icon: Target, component: <HealthGoals setActiveComponent={setActiveComponent} /> },
    ];

    const billingItems = [
        { id: "purchases", icon: CreditCard, label: "Purchases & Plans", component: <PurchasesPlans /> },
        { id: "paymentHistory", icon: Receipt, label: "Payment History", component: <PaymentHistory /> },
        { id: "support", icon: CircleHelp, label: "Support / Enquiry", component: <SupportEnquiry /> },
    ];

    const generateTimeSlots = () => {
        const slots = [];
        const startHour = 10;
        const endHour = 19;

        for (let hour = startHour; hour <= endHour; hour++) {
            const maxMinute = hour === endHour ? 0 : 30;

            for (let minute = 0; minute <= maxMinute; minute += 30) {
                const hour12 = hour % 12 || 12;
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const formattedTime = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
                const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

                slots.push({
                    value: timeValue,
                    display: formattedTime
                });
            }
        }

        return slots;
    };

    const isTimeDisabled = (timeSlot) => {
        if (!followUpSelectedDate) return true;

        const todayDate = new Date();
        const selectedDateObj = new Date(followUpSelectedDate);

        if (selectedDateObj.toDateString() !== todayDate.toDateString()) {
            return false;
        }

        const [hours, minutes] = timeSlot.value.split(':').map(Number);
        const slotTime = new Date();
        slotTime.setHours(hours, minutes, 0, 0);

        const currentTime = new Date();
        currentTime.setHours(currentTime.getHours(), currentTime.getMinutes(), 0, 0);

        return slotTime < currentTime;
    };

    const allTimeSlots = generateTimeSlots();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setDashboardLoading(true);
                const response = await getWebDashboard();

                if (response && response.status === true) {
                    setDashboardData(response);
                    setDashboardError(null);
                } else {
                    setDashboardError(response?.message || "Failed to fetch dashboard data");
                }
            } catch (error) {
                console.error("Error fetching dashboard:", error);
                setDashboardError(error.message);
            } finally {
                setDashboardLoading(false);
            }
        };

        if (!authChecking) {
            fetchDashboardData();
        }
    }, [authChecking]);

    const downloadCSV = () => {
        if (!dashboardData?.purchases || dashboardData.purchases.length === 0) {
            showToast("No payment data available to download.", "error");
            return;
        }

        const headers = ["Transaction ID", "Date", "Amount", "Status"];

        const rows = dashboardData.purchases.map(purchase => [
            purchase.transaction_id || `#TRX-${purchase.id}`,
            new Date(purchase.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            parseFloat(purchase.total_amount || purchase.amount || 0).toFixed(2),
            purchase.payment_status === 'completed' ? 'Successful' : 'Pending'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `payment_history_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showToast("CSV downloaded successfully!", "success");
    };

useEffect(() => {
    const handleProfileImageUpdate = (event) => {
        if (event.detail?.imageUrl) {
        
            setUserData(prev => {
                if (prev) {
                    return { ...prev, profile_img: event.detail.imageUrl };
                }
                return prev;
            });
            const user = getUserData();
            if (user) {
                user.profile_img = event.detail.imageUrl;
            }
        }
    };

    const handleStorageChange = (event) => {
        if (event.key === 'userData' || event.key === 'authData') {
            const updatedUser = getUserData();
            if (updatedUser) {
                setUserData(updatedUser);
            }
        }
    };

    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    window.addEventListener('storage', handleStorageChange);

    return () => {
        window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
        window.removeEventListener('storage', handleStorageChange);
    };
}, []);

    const handleSupportChange = (e) => {
        const { name, value } = e.target;
        setSupportForm({
            ...supportForm,
            [name]: value,
        });
    };

    const handleSupportSubmit = async () => {
        if (!supportForm.subject.trim()) {
            showToast("Please enter a subject", "error");
            return;
        }
        if (!supportForm.message.trim()) {
            showToast("Please enter your message", "error");
            return;
        }

        setSupportLoading(true);

        try {
            const inquiryData = {
                subject: supportForm.subject,
                notes: supportForm.message,
            };

            const response = await appointmentInquiry(inquiryData);

            if (response && response.status === true) {
                showToast("Message sent successfully! Our team will get back to you soon.", "success");
                setSupportForm({ subject: "", message: "" });
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            } else {
                showToast(response?.message || "Failed to send message. Please try again.", "error");
            }
        } catch (err) {
            console.error("Error submitting inquiry:", err);
            showToast(err.message || "Failed to send message. Please try again.", "error");
        } finally {
            setSupportLoading(false);
        }
    };

    const handleFollowUpDateSelect = (dateStr) => {
        setFollowUpSelectedDate(dateStr);
        setFollowUpSelectedTime(null);
    };

    const handleFollowUpTimeSelect = (timeSlot) => {
        setFollowUpSelectedTime(timeSlot);
    };

    const handleFollowUpModeSelect = (mode) => {
        setFollowUpMode(mode);
    };

    const handleFollowUpBooking = async () => {
        if (!followUpSelectedDate) {
            showToast("Please select a date", "error");
            return;
        }
        if (!followUpSelectedTime) {
            showToast("Please select a time", "error");
            return;
        }

        setFollowUpLoading(true);

        try {
            const followUpData = {
                mode_consultation: followUpMode === "Video Call" ? "Video" : "Voice",
                appointment_date: followUpSelectedDate,
                appointment_time: followUpSelectedTime.value,
                comments: "Follow-up session",
                type: "followup-session"
            };
            const response = await createFollowUp(followUpData);

            if (response && (response.status === true || response.success === true)) {
                showToast("Follow-up session booked successfully!", "success");
                setFollowUpSelectedDate(null);
                setFollowUpSelectedTime(null);
                setFollowUpMode("Video Call");
            } else {
                showToast(response?.message || "Failed to book follow-up session. Please try again.", "error");
            }
        } catch (error) {
            console.error("Booking error:", error);
            showToast(error.message || "Failed to book follow-up session. Please try again.", "error");
        } finally {
            setFollowUpLoading(false);
        }
    };

    const handleLogout = () => {
        clearAuthData();
        setIsUserMenuOpen(false);
        navigate("/login");
    };

    const handleEditProfile = () => {
        setActiveComponent("personalInfo");
        setIsProfileOpen(true);
    };

    const getInitials = (name) => {
        if (!name) return "U";

        const nameParts = name.trim().split(' ');

        if (nameParts.length === 1) {
            return nameParts[0].charAt(0).toUpperCase();
        }

        const firstInitial = nameParts[0].charAt(0).toUpperCase();
        const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();

        return firstInitial + lastInitial;
    };

    const fileInputRef = useRef(null);
    const userMenuRef = useRef(null);

    const handleAttachmentClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            showToast(`File "${file.name}" selected successfully!`, "success");
        }
    };

    const handleNavigation = (id) => {
        if (id === "appointments" && activeComponent === "appointmentdetails") {
            setActiveComponent("appointments");
            setSelectedAppointmentForDetails(null);
        } else {
            setActiveComponent(id);
        }
        setIsMobileMenuOpen(false);
        if (id === "profile") {
            setIsProfileOpen(true);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                userMenuRef.current &&
                !userMenuRef.current.contains(event.target)
            ) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleProfileSubItemClick = (id) => {
        setActiveComponent(id);
        setIsMobileMenuOpen(false);
        setIsProfileOpen(true);
    };

    const toggleProfileDropdown = (e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        setIsProfileOpen(!isProfileOpen);
    };

    const getStatusColor = (status) => {
        if (status === 0) return "bg-[#f8c96c] text-[#4e3a06]";
        if (status === 1) return "bg-green-500 text-white";
        return "bg-gray-300 text-gray-600";
    };

    const getStatusText = (status) => {
        if (status === 0) return "Upcoming";
        if (status === 1) return "Completed";
        return "Cancelled";
    };

    const isProfileActive = () => {
        const profileIds = ["personalInfo", "medicalHistory", "lifestyle", "dietaryPreferences", "healthGoals"];
        return profileIds.includes(activeComponent);
    };

    const renderActiveComponent = () => {
        const profileItem = profileMenuItems.find(item => item.id === activeComponent);

        switch (activeComponent) {
            case "dashboard":
                return (
                    <>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2 font-lato">
                            Dashboard Overview
                        </h2>
                        <p className="text-gray-400 text-sm mb-8 font-lato">
                            Welcome back, {userData?.name || "Guest"}. Here's a summary of your health journey.
                        </p>

                        {dashboardLoading ? (
                            <div className="flex justify-center items-center py-20">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
                            </div>
                        ) : dashboardError ? (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                                <p className="text-red-500">{dashboardError}</p>
                            </div>
                        ) : dashboardData && (
                            <>
                                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 font-lato">
                                    <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-5">
                                            <User size={15} className="text-green-500" />
                                            <h3 className="text-[15px] font-semibold text-[#24312d]">
                                                Profile Summary
                                            </h3>
                                        </div>

                                        <div className="text-center mb-5">
{userData?.profile_img ? (
    <img
        src={userData.profile_img}
        alt="user"
        className="w-9 h-9 rounded-full object-cover"
        onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `
                <div class="w-9 h-9 rounded-full bg-[#57b26a] flex items-center justify-center">
                    <span class="text-white text-sm font-medium">${getInitials(userData?.name)}</span>
                </div>
            `;
        }}
    />
) : (
    <div className="w-9 h-9 rounded-full bg-[#57b26a] flex items-center justify-center">
        <span className="text-white text-sm font-medium">
            {getInitials(userData?.name)}
        </span>
    </div>
)}
                                            <h4 className="text-[18px] font-semibold text-[#24312d] leading-none">
                                                {dashboardData?.dietPlan_details?.clientInfo?.name || userData?.name}
                                            </h4>
                                            <p className="text-[13px] text-[#98a19b] mt-2">
                                                {dashboardData?.dietPlan_details?.clientInfo?.age || "—"} Years Old • {dashboardData?.dietPlan_details?.clientInfo?.gender || "—"}
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                            <div className="bg-[#f7f9f7] rounded-lg p-3">
                                                <p className="text-[11px] text-[#9aa39d] mb-1">Current BMI</p>
                                                <h5 className="text-[15px] font-semibold text-[#24312d]">
                                                    {dashboardData?.dietPlan_details?.clientInfo?.bmi || "—"} <span className="font-normal">(Normal)</span>
                                                </h5>
                                            </div>
                                            <div className="bg-[#f7f9f7] rounded-lg p-3">
                                                <p className="text-[11px] text-[#9aa39d] mb-1">Diet Type</p>
                                                <h5 className="text-[15px] font-semibold text-[#24312d]">
                                                    {dashboardData?.dietPlan_details?.configData?.mealTypes
                                                        ? dashboardData.dietPlan_details.configData.mealTypes.split(',')[0].trim()
                                                        : "—"}
                                                </h5>
                                            </div>
                                        </div>

                                        <div className="bg-[#f7f9f7] rounded-lg p-3 mb-4">
                                            <p className="text-[11px] text-[#9aa39d] mb-1">Primary Goal</p>
                                            <h5 className="text-[15px] font-semibold text-[#24312d]">
                                                {dashboardData?.dietPlan_details?.clientInfo?.goal || "—"}
                                            </h5>
                                        </div>

                                        <button
                                            onClick={handleEditProfile}
                                            className="w-full h-[42px] bg-[#f7f9f7] border border-[#dfe5df] rounded-lg text-[14px] text-[#4d5954] hover:bg-gray-50 transition flex items-center justify-center gap-2"
                                        >
                                            <Pencil size={16} />
                                            Edit Profile
                                        </button>
                                    </div>
                                    <div className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5">
                                        <div className="flex items-center justify-between mb-5">
                                            <div className="flex items-center gap-2">
                                                <CalendarDays size={15} className="text-green-500" />
                                                <h3 className="text-[15px] font-semibold text-[#24312d]">
                                                    Upcoming Appointments
                                                </h3>
                                            </div>
                                            <button
                                                onClick={() => handleNavigation("appointments")}
                                                className="text-[12px] text-[#57b26a] border border-gray-200 px-3 py-1 rounded-md hover:bg-gray-50 transition"
                                            >
                                                View All
                                            </button>
                                        </div>

                                        {dashboardData?.appointments?.data?.length > 0 ? (
                                            dashboardData.appointments.data.slice(0, 2).map((appointment, index) => {
                                                const isUpcoming = appointment.status === 0;
                                                return (
                                                    <div key={appointment.id} className={`border border-[#edf1ed] rounded-lg p-4 mb-4 ${!isUpcoming ? 'opacity-70' : ''} bg-[#f7f9f7]`}>
                                                        <div className="flex items-center justify-between flex-wrap gap-4">
                                                            <div className="flex items-center gap-4">
                                                                <div className="w-[58px] h-[58px] bg-white rounded-md flex flex-col items-center justify-center">
                                                                    <span className="text-[10px] text-[#57b26a] font-semibold">
                                                                        {new Date(appointment.appointment_date).toLocaleString('default', { month: 'short' }).toUpperCase()}
                                                                    </span>
                                                                    <span className="text-[22px] font-semibold text-[#24312d] leading-none">
                                                                        {new Date(appointment.appointment_date).getDate()}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-[15px] font-semibold text-[#24312d] mb-1">
                                                                        {appointment.purpose || "Consultation"}
                                                                    </h4>
                                                                    <p className="text-[13px] text-[#97a09a] flex items-center gap-1">
                                                                        <User size={12} />
                                                                        {appointment.name || "Patient"} &nbsp; • &nbsp;
                                                                        <Clock size={12} />
                                                                        {appointment.appointment_time}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <span className={`text-[12px] font-bold px-3 py-2 rounded-full ${getStatusColor(appointment.status)}`}>
                                                                    {getStatusText(appointment.status)}
                                                                </span>
                                                                {isUpcoming && appointment.mode_consultation === "Video Call" && (
                                                                    <button className="bg-[#57b26a] hover:bg-[#57b26a] text-white text-[13px] px-4 h-[36px] rounded-md flex items-center justify-center gap-2">
                                                                        <Video size={16} />
                                                                        Join Call
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="border border-[#edf1ed] rounded-lg p-8 text-center bg-[#f7f9f7]">
                                                <p className="text-gray-500">No appointments found</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {dashboardData?.dietPlan_details && (
                                    <div className="bg-white border border-[#e8ece8] rounded-xl p-5 mb-6 font-lato">
                                        <div className="flex items-center justify-between mb-5">
                                            <div className="flex items-center gap-2">
                                                <Utensils size={15} className="text-green-500" />
                                                <h3 className="text-[15px] font-semibold text-[#24312d]">Active Diet Plan</h3>
                                            </div>
                                            <span className="text-[11px] bg-[#57b26a] text-white px-3 py-1 rounded-full">
                                                {dashboardData.dietPlan_details.planInfo?.status === "draft" ? "Draft" : "Active"}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                            <div className="xl:col-span-1 border border-[#edf1ed] bg-[#f7f9f7] rounded-lg p-4">
                                                <h4 className="text-lg sm:text-xl lg:text-[22px] font-semibold text-[#24312d] mb-2 leading-none">
                                                    {dashboardData.dietPlan_details.planInfo?.title || "Diet Plan"}
                                                </h4>
                                                <p className="text-[13px] text-[#98a19b] leading-6 mb-5">
                                                    {dashboardData.dietPlan_details.planInfo?.description || "No description"}
                                                </p>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[13px] font-medium text-[#24312d]">Weekly Adherence</span>
                                                    <span className="text-[13px] text-[#98a19b]">Day 4 of 7</span>
                                                </div>
                                                <div className="w-full h-[6px] bg-[#edf1ed] rounded-full mb-5 overflow-hidden">
                                                    <div className="w-[65%] h-full bg-[#57b26a] rounded-full"></div>
                                                </div>
                                                {dashboardData?.dietPlan_details?.planInfo?.pdf_link && (
                                                    <a
                                                        href={dashboardData.dietPlan_details.planInfo.pdf_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-[14px] text-green-500 hover:bg-green-50 transition flex items-center justify-center"
                                                    >
                                                        Download Full PDF Guide
                                                    </a>
                                                )}
                                            </div>

                                            <div className="xl:col-span-2">
                                                <h4 className="text-[14px] font-semibold text-[#24312d] mb-4">Today's Meals</h4>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    {Object.keys(dashboardData.dietPlan_details.mealData?.["Day 1"] || {}).slice(0, 3).map((mealKey, index) => {
                                                        const meal = dashboardData.dietPlan_details.mealData["Day 1"][mealKey];
                                                        const mealIcons = [Coffee, Salad, UtensilsCrossed];
                                                        const Icon = mealIcons[index] || UtensilsCrossed;
                                                        const mealNames = ["Breakfast", "Lunch", "Dinner"];

                                                        return (
                                                            <div key={index} className="border border-[#edf1ed] rounded-lg p-4">
                                                                <div className="w-9 h-9 rounded-full bg-[#f7f9f7] flex items-center justify-center text-green-500 text-lg mb-4">
                                                                    <Icon size={18} />
                                                                </div>
                                                                <h5 className="text-lg sm:text-xl lg:text-[22px] font-semibold text-[#24312d] mb-2">
                                                                    {mealNames[index] || mealKey}
                                                                </h5>
                                                                <p className="text-[13px] text-[#98a19b] leading-6 mb-4">
                                                                    {meal?.primary?.name || "No meal plan"}
                                                                </p>
                                                                <span className="text-[14px] text-green-500 font-medium">
                                                                    {meal?.primary?.calories || 0} kcal
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 font-lato">
                                    <div className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-5">
                                            <PhoneCall size={15} className="text-green-500" />
                                            <h3 className="text-[15px] font-semibold text-[#24312d]">Book Follow-Up Session</h3>
                                        </div>

                                        <div className="mb-5">
                                            <div className="flex items-center gap-3 mb-3">
                                                <span className="w-7 h-7 rounded-full bg-[#57b26a] text-white text-[13px] flex items-center justify-center font-medium">1</span>
                                                <p className="text-[14px] font-medium text-[#24312d]">Select Date</p>
                                            </div>
                                            <div className="ml-10">
                                                <input
                                                    type="date"
                                                    onChange={(e) => handleFollowUpDateSelect(e.target.value)}
                                                    className="w-full h-[42px] border border-[#e1e6e1] rounded-lg px-4 text-[13px] text-[#24312d] bg-white cursor-pointer focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a] outline-none"
                                                    min={new Date().toISOString().split('T')[0]}
                                                    value={followUpSelectedDate || ""}
                                                />
                                                {followUpSelectedDate && (
                                                    <p className="text-[12px] text-[#57b26a] mt-1">
                                                        Selected: {new Date(followUpSelectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-5">
                                            <div className="flex items-center gap-3 mb-3">
                                                <span className="w-7 h-7 rounded-full bg-[#57b26a] text-white text-[13px] flex items-center justify-center font-medium">2</span>
                                                <p className="text-[14px] font-medium text-[#24312d]">Select Time (10 AM - 7 PM)</p>
                                            </div>
                                            <div className="ml-10">
                                                {!followUpSelectedDate ? (
                                                    <div className="text-center py-4 text-[#98a19b] text-sm border border-dashed border-[#e1e6e1] rounded-lg">
                                                        Please select a date first
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                                        {allTimeSlots.map((slot, index) => {
                                                            const disabled = isTimeDisabled(slot);
                                                            const isActive = followUpSelectedTime && followUpSelectedTime.value === slot.value;

                                                            return (
                                                                <button
                                                                    key={index}
                                                                    onClick={() => !disabled && handleFollowUpTimeSelect(slot)}
                                                                    disabled={disabled}
                                                                    className={`h-[38px] rounded-lg text-[13px] border transition ${disabled
                                                                        ? "bg-gray-50 border-gray-200 text-[#d1d6d2] cursor-not-allowed"
                                                                        : isActive
                                                                            ? "border-green-500 bg-green-50 text-green-600 font-medium"
                                                                            : "border-[#e1e6e1] text-[#a0a8a2] hover:border-green-400"
                                                                        }`}
                                                                >
                                                                    {slot.display}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-5">
                                            <div className="flex items-center gap-3 mb-3">
                                                <span className="w-7 h-7 rounded-full bg-[#57b26a] text-white text-[13px] flex items-center justify-center font-medium">3</span>
                                                <p className="text-[14px] font-medium text-[#24312d]">Choose Mode</p>
                                            </div>
                                            <div className="ml-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <button
                                                    onClick={() => handleFollowUpModeSelect("Video Call")}
                                                    className={`relative h-[42px] border rounded-lg px-4 flex items-center gap-2 text-[14px] transition ${followUpMode === "Video Call"
                                                        ? "border-green-500 bg-green-50 text-[#24312d]"
                                                        : "border-[#e1e6e1] text-[#a0a8a2] hover:border-green-400"
                                                        }`}
                                                >
                                                    <Video size={16} />
                                                    <span>Video Call</span>
                                                    {followUpMode === "Video Call" && (
                                                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleFollowUpModeSelect("Voice Call")}
                                                    className={`relative h-[42px] border rounded-lg px-4 flex items-center gap-2 text-[14px] transition ${followUpMode === "Voice Call"
                                                        ? "border-green-500 bg-green-50 text-[#24312d]"
                                                        : "border-[#e1e6e1] text-[#a0a8a2] hover:border-green-400"
                                                        }`}
                                                >
                                                    <Phone size={16} />
                                                    <span>Voice Call</span>
                                                    {followUpMode === "Voice Call" && (
                                                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="ml-10">
                                            <button
                                                onClick={handleFollowUpBooking}
                                                disabled={followUpLoading || !followUpSelectedDate || !followUpSelectedTime}
                                                className={`text-white px-6 h-[40px] rounded-md text-[14px] transition ${followUpLoading || !followUpSelectedDate || !followUpSelectedTime
                                                    ? "bg-gray-400 cursor-not-allowed"
                                                    : "bg-[#57b26a] hover:bg-green-600"
                                                    }`}
                                            >
                                                {followUpLoading ? "Booking..." : "Confirm Booking"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-5">
                                            <Receipt size={15} className="text-green-500" />
                                            <h3 className="text-[15px] font-semibold text-[#24312d]">Plan Purchase Details</h3>
                                        </div>
                                        <div className="border border-[#edf1ed] rounded-xl bg-[#f7f9f7] p-5 text-center">
                                            {dashboardData?.purchases && dashboardData.purchases.length > 0 ? (
                                                <>
                                                    <div className="w-14 h-14 rounded-full bg-white mx-auto flex items-center justify-center text-green-500 text-xl mb-4">
                                                        <Crown size={24} />
                                                    </div>
                                                    <h4 className="text-xl sm:text-2xl lg:text-[26px] font-semibold text-[#24312d] mb-3">
                                                        {dashboardData.purchases[0]?.program?.name || "Premium Wellness Pro"}
                                                    </h4>
                                                    <span className="inline-block text-[12px] bg-[#57b26a] text-white px-4 py-1 rounded-full mb-5">
                                                        {dashboardData.purchases[0]?.status === "active" ? "Active Subscription" : "Inactive"}
                                                    </span>
                                                    <div className="border-t border-[#edf1ed] pt-4 text-left space-y-3 mb-5">
                                                        <div className="flex justify-between text-[14px]">
                                                            <span className="text-[#98a19b]">Plan Duration</span>
                                                            <span className="text-[#24312d] font-medium">
                                                                {dashboardData.purchases[0]?.variant?.duration || "3 Months"}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-[14px]">
                                                            <span className="text-[#98a19b]">Renewal Date</span>
                                                            <span className="text-[#24312d] font-medium">
                                                                {dashboardData.purchases[0]?.end_date ? new Date(dashboardData.purchases[0].end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "Nov 12, 2024"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleNavigation("purchases")}
                                                        className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-[14px] text-green-500 transition flex items-center justify-center gap-2"
                                                    >
                                                        <span>Manage Subscription</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="py-8">
                                                    <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto flex items-center justify-center text-gray-400 text-3xl mb-4">
                                                        <Crown size={32} className="text-gray-300" />
                                                    </div>
                                                    <h4 className="text-lg font-semibold text-[#24312d] mb-2">No Plan Purchased</h4>
                                                    <p className="text-[13px] text-[#98a19b] mb-5">
                                                        You haven't purchased any plan yet.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 font-lato">
                                    <div className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5">
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="flex items-center gap-2">
                                                <Receipt size={15} className="text-green-500" />
                                                <h3 className="text-[15px] font-semibold text-[#24312d]">Payment History</h3>
                                            </div>
                                            <button
                                                onClick={downloadCSV}
                                                className="text-[12px] text-green-500 border border-[#dfe5df] px-4 py-1.5 rounded-md hover:bg-green-50 transition"
                                            >
                                                Download CSV
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <div className="min-w-[650px]">
                                                <div className="grid grid-cols-4 text-[12px] text-[#98a19b] pb-3 border-b border-[#edf1ed] mb-2">
                                                    <span>Transaction ID</span>
                                                    <span>Date</span>
                                                    <span>Amount</span>
                                                    <span>Status</span>
                                                </div>
                                                {dashboardData?.purchases?.length > 0 ? (
                                                    dashboardData.purchases.slice(0, 3).map((purchase, index) => (
                                                        <div key={purchase.id} className="grid grid-cols-4 items-center py-4 border-b border-[#f4f6f4] last:border-0">
                                                            <span className="text-[13px] text-[#b1b8b3]">{purchase.transaction_id || `#TRX-${purchase.id}`}</span>
                                                            <span className="text-[14px] text-[#24312d]">
                                                                {new Date(purchase.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                            <span className="text-[14px] font-medium text-[#24312d]">
                                                                ₹{parseFloat(purchase.total_amount || purchase.amount || 0).toLocaleString('en-IN')}
                                                            </span>
                                                            <span className={`inline-flex w-fit text-[11px] px-3 py-1 rounded-full ${purchase.payment_status === 'completed' ? 'bg-[#57b26a] text-white' : 'bg-yellow-500 text-white'}`}>
                                                                {purchase.payment_status === 'completed' ? 'Successful' : 'Pending'}
                                                            </span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="py-8 text-center text-gray-500">No payment history found</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-5">
                                            <CircleHelp size={15} className="text-green-500" />
                                            <h3 className="text-[15px] font-semibold text-[#24312d]">Enquiry / Support</h3>
                                        </div>

                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            className="hidden"
                                            accept=".pdf,.doc,.docx,.jpg,.png,.txt"
                                        />

                                        <div className="mb-4">
                                            <label className="block text-[13px] font-medium text-[#24312d] mb-2">Subject</label>
                                            <input
                                                type="text"
                                                name="subject"
                                                value={supportForm.subject}
                                                onChange={handleSupportChange}
                                                placeholder="Briefly describe your issue"
                                                className="w-full h-[42px] border border-[#e1e6e1] rounded-lg px-4 text-[14px] text-[#24312d] placeholder-[#b1b8b3] outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a]"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label className="block text-[13px] font-medium text-[#24312d] mb-2">Message</label>
                                            <textarea
                                                name="message"
                                                value={supportForm.message}
                                                onChange={handleSupportChange}
                                                rows="4"
                                                placeholder="How can we help you today?"
                                                className="w-full border border-[#e1e6e1] rounded-lg px-4 py-3 text-[13px] text-[#24312d] placeholder-[#b0b7b1] outline-none resize-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a]"
                                            />
                                        </div>

                                        <button
                                            onClick={handleAttachmentClick}
                                            className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-[14px] text-[#4d5954] hover:bg-gray-50 transition mb-4 flex items-center justify-center gap-2"
                                        >
                                            <Paperclip size={16} />
                                            Add Attachment
                                        </button>

                                        <button
                                            onClick={handleSupportSubmit}
                                            disabled={supportLoading}
                                            className="w-full h-[42px] bg-[#57b26a] hover:bg-green-600 text-white rounded-lg text-[14px] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {supportLoading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    Sending...
                                                </>
                                            ) : (
                                                "Submit Enquiry"
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                );
            case "appointments":
            case "appointmentdetails":
                return <MyAppointments />;
            case "dietplans":
                return <DietPlans />;
            case "followup":
                return <FollowupSessions userData={userData} />;
            case "personalInfo":
            case "medicalHistory":
            case "lifestyle":
            case "dietaryPreferences":
            case "healthGoals":
                if (profileItem) {
                    return React.cloneElement(profileItem.component, {
                        setActiveComponent: setActiveComponent
                    });
                }
                return <PersonalInfo setActiveComponent={setActiveComponent} />;
            case "purchases":
                return <PurchasesPlans />;
            case "paymentHistory":
                return <PaymentHistory />;
            case "support":
                return <SupportEnquiry />;
            case "mycourses":
                return <MyCourses />;
            case "courseplayer":
                return <CoursePlayer slug={courseSlug} />;
            case "learningresources":
                return <LearningResources onNavigateToDigitalLibrary={() => handleNavigation("digitallibrary")} />;
            case "digitallibrary":
                return <DigitalLibrary />;
            case "assessment":
                return <Assessment assessmentData={assessmentData} />;

            default:
                return <DashboardOverview />;
        }
    };

    if (authChecking) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#f8faf8] font-sans font-lato">
            <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-lg border border-gray-100"
            >
                <Menu size={20} className="text-gray-600" />
            </button>
            <div className={`fixed inset-0 z-50 lg:hidden transition-transform duration-300 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)} />
                <div className="relative w-[280px] h-full bg-white shadow-xl flex flex-col">
                    <div className="px-5 pt-6 pb-4 border-b border-gray-100 flex justify-between items-center">
                        <Link to="/" className="flex items-center">
                            <img
                                src={require('../../../src/assets/images/newlogo.png')}
                                alt="Logo"
                                className="h-auto w-[110px]"
                            />
                        </Link>
                        <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-lg bg-gray-100">
                            <X size={18} className="text-gray-600" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                        <div className="mb-6">
                            <p className="text-xs text-gray-400 uppercase mb-3 tracking-wide">Menu</p>
                            <div className="space-y-1">
                                {menuItems.map((item, index) => {
                                    const Icon = item.icon;
                                    const isProfileItemActive = item.id === "profile" && isProfileActive();
                                    const isLearningResourcesActive = item.id === "learningresources" &&
                                        (activeComponent === "learningresources" || activeComponent === "digitallibrary");
                                    const isAppointmentsActive = item.id === "appointments" &&
                                        (activeComponent === "appointments" || activeComponent === "appointmentdetails");
                                    const isMyCoursesActive = item.id === "mycourses" &&
                                        (activeComponent === "mycourses" || activeComponent === "courseplayer" || activeComponent === "assessment");
                                    const isActive = activeComponent === item.id || isProfileItemActive || isLearningResourcesActive || isAppointmentsActive || isMyCoursesActive;

                                    return (
                                        <div key={index}>
                                            <button
                                                onClick={() => {
                                                    if (item.hasDropdown) {
                                                        if (item.id === "profile") {
                                                            toggleProfileDropdown();
                                                        } else if (item.id === "learningresources") {
                                                            toggleLearningResourcesDropdown();
                                                        }
                                                    } else {
                                                        handleNavigation(item.id);
                                                    }
                                                }}
                                                className={`w-full flex items-center justify-between gap-1 px-4 py-3 rounded-xl text-sm font-medium transition ${isActive
                                                    ? "bg-green-50 text-[#57b26a]"
                                                    : "text-gray-600 hover:bg-gray-50"
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3 font-bold" onClick={() => {
                                                    if (item.id === "learningresources") {
                                                        handleNavigation("learningresources");
                                                    }
                                                }}>
                                                    <Icon size={18} />
                                                    {item.label}
                                                </div>
                                                {item.hasDropdown && (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        {(item.id === "profile" && isProfileOpen) ||
                                                            (item.id === "learningresources" && isLearningResourcesOpen)
                                                            ? <ChevronDown size={16} />
                                                            : <ChevronRight size={16} />}
                                                    </div>
                                                )}
                                            </button>

                                            {item.hasDropdown && item.id === "profile" && isProfileOpen && (
                                                <div className="ml-6 mt-2 space-y-1">
                                                    {profileMenuItems.map((profileItem, idx) => {
                                                        const Icon = profileItem.icon;
                                                        const isSubActive = activeComponent === profileItem.id;

                                                        return (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleProfileSubItemClick(profileItem.id)}
                                                                className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition ${isSubActive
                                                                    ? "bg-green-50 text-[#57b26a]"
                                                                    : "text-gray-600 hover:bg-gray-50 hover:text-[#57b26a]"
                                                                    }`}
                                                            >
                                                                <Icon size={16} />
                                                                {profileItem.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {item.hasDropdown && item.id === "learningresources" && isLearningResourcesOpen && (
                                                <div className="ml-6 mt-2 space-y-1">
                                                    {learningResourcesSubItems.map((subItem, idx) => {
                                                        const Icon = subItem.icon;
                                                        const isSubActive = activeComponent === subItem.id;

                                                        return (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleNavigation(subItem.id)}
                                                                className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition ${isSubActive
                                                                    ? "bg-green-50 text-[#57b26a]"
                                                                    : "text-gray-600 hover:bg-gray-50 hover:text-[#57b26a]"
                                                                    }`}
                                                            >
                                                                <Icon size={16} />
                                                                {subItem.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <p className="text-xs text-gray-400 uppercase mb-3 tracking-wide">Billing & Support</p>
                            <div className="space-y-1">
                                {billingItems.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => handleNavigation(item.id)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition ${activeComponent === item.id
                                                ? "bg-green-50 text-[#57b26a] "
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            <Icon size={18} />
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <aside className="hidden lg:block bg-[#f4f6f5] fixed left-0 top-0 h-full w-[270px] bg-white border-r border-gray-200 overflow-y-auto">
                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                    <Link to="/" className="flex items-center">
                        <img
                            src={require('../../../src/assets/images/newlogo.png')}
                            alt="Logo"
                            className="h-auto w-[125px]"
                        />
                    </Link>
                </div>
                <div className="px-6 py-4">
                    <div className="mb-8">
                        <p className="text-xs text-gray-400 uppercase mb-4 tracking-wide">Menu</p>
                        <div className="space-y-2">
                            {menuItems.map((item, index) => {
                                const Icon = item.icon;
                                const isProfileItemActive = item.id === "profile" && isProfileActive();
                                const isLearningResourcesActive = item.id === "learningresources" &&
                                    (activeComponent === "learningresources" || activeComponent === "digitallibrary");
                                const isAppointmentsActive = item.id === "appointments" &&
                                    (activeComponent === "appointments" || activeComponent === "appointmentdetails");
                                const isMyCoursesActive = item.id === "mycourses" &&
                                    (activeComponent === "mycourses" || activeComponent === "courseplayer" || activeComponent === "assessment");
                                const isActive = activeComponent === item.id || isProfileItemActive || isLearningResourcesActive || isAppointmentsActive || isMyCoursesActive;

                                return (
                                    <div key={index}>
                                        <button
                                            onClick={() => {
                                                if (item.hasDropdown) {
                                                    if (item.id === "profile") {
                                                        toggleProfileDropdown();
                                                    } else if (item.id === "learningresources") {
                                                        toggleLearningResourcesDropdown();
                                                    }
                                                } else {
                                                    handleNavigation(item.id);
                                                }
                                            }}
                                            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${isActive
                                                ? "bg-green-50 text-[#57b26a] "
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            <div className="flex items-center gap-3" onClick={() => {
                                                if (item.id === "learningresources") {
                                                    handleNavigation("learningresources");
                                                }
                                            }}>
                                                <Icon size={18} />
                                                {item.label}
                                            </div>
                                            {item.hasDropdown && (
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    {(item.id === "profile" && isProfileOpen) ||
                                                        (item.id === "learningresources" && isLearningResourcesOpen)
                                                        ? <ChevronDown size={14} />
                                                        : <ChevronRight size={14} />}
                                                </div>
                                            )}
                                        </button>

                                        {item.hasDropdown && item.id === "profile" && isProfileOpen && (
                                            <div className="ml-8 mt-1 space-y-1">
                                                {profileMenuItems.map((profileItem, idx) => {
                                                    const Icon = profileItem.icon;
                                                    const isSubActive = activeComponent === profileItem.id;

                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleProfileSubItemClick(profileItem.id)}
                                                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${isSubActive
                                                                ? "bg-green-50 text-[#57b26a]"
                                                                : "text-gray-600 hover:bg-gray-50"
                                                                }`}
                                                        >
                                                            <Icon size={14} />
                                                            {profileItem.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {item.hasDropdown && item.id === "learningresources" && isLearningResourcesOpen && (
                                            <div className="ml-8 mt-1 space-y-1">
                                                {learningResourcesSubItems.map((subItem, idx) => {
                                                    const Icon = subItem.icon;
                                                    const isSubActive = activeComponent === subItem.id;

                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleNavigation(subItem.id)}
                                                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${isSubActive
                                                                ? "bg-green-50 text-[#57b26a]"
                                                                : "text-gray-600 hover:bg-gray-50"
                                                                }`}
                                                        >
                                                            <Icon size={14} />
                                                            {subItem.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <p className="text-xs text-gray-400 font-bold uppercase mb-4 tracking-wide">Billing & Support</p>
                        <div className="space-y-2">
                            {billingItems.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={index}
                                        onClick={() => handleNavigation(item.id)}
                                        className={`w-full font-bold flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition ${activeComponent === item.id
                                            ? "bg-green-50 text-[#57b26a]"
                                            : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                    >
                                        <Icon size={18} />
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </aside>
            <div className="lg:ml-[270px] flex flex-col min-h-screen">
                <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="w-10 lg:hidden"></div>
                        <div className="relative flex-1 max-w-[200px] sm:max-w-[300px] lg:max-w-[420px]">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="w-full pl-9 pr-3 h-9 lg:h-10 rounded-lg border border-gray-100 outline-none text-sm placeholder:text-xs lg:placeholder:text-sm focus:border-green-400 focus:ring-1 focus:ring-green-400"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Notification Bell */}
                            <div className="relative">
                                <button
                                    onClick={toggleNotificationDropdown}
                                    className="relative flex h-10 w-10 items-center justify-center rounded-xl border-2 border-[#15803d] bg-white hover:bg-green-50 transition"
                                >
                                    <Bell size={18} className="text-[#15803d]" />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#15803d] text-[10px] font-semibold text-white">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </button>

                                {showNotificationDropdown && (
                                    <NotificationDropdown
                                        onClose={() => setShowNotificationDropdown(false)}
                                        onMarkAsRead={handleMarkAllAsRead}
                                        onUnreadCountUpdate={handleUnreadCountUpdate}
                                    />
                                )}
                            </div>

                            {/* User Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <div
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="flex items-center gap-2 cursor-pointer select-none"
                                >
                                    {userData?.profile_img ? (
                                        <img
                                            src={userData.profile_img}
                                            alt="user"
                                            className="w-9 h-9 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 rounded-full bg-[#57b26a] flex items-center justify-center">
                                            <span className="text-white text-sm font-medium">
                                                {getInitials(userData?.name)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="hidden sm:block">
                                        <p className="font-medium text-gray-800 text-sm">
                                            {userData?.name}
                                        </p>
                                        {userData?.role && (
                                            <p className="text-xs text-gray-400">Customer</p>
                                        )}
                                    </div>

                                    <ChevronDown
                                        size={16}
                                        className={`text-gray-400 transition ${isUserMenuOpen ? "rotate-180" : ""}`}
                                    />
                                </div>

                                {isUserMenuOpen && (
                                    <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                                        <button
                                            onClick={() => {
                                                setIsUserMenuOpen(false);
                                                setActiveComponent("personalInfo");
                                                setIsProfileOpen(true);
                                            }}
                                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            My Profile
                                        </button>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-50"
                                        >
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>
                <main className={`flex-1 ${activeComponent === "courseplayer" || activeComponent === "mycourses" ? "" : "p-4 sm:p-6 lg:p-8"}`}>
                    {renderActiveComponent()}
                </main>
            </div>
        </div>
    );
};

export default DashboardOverview;